import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiConfigKey } from '@bruno/shared';
import { Button, Card } from '@bruno/ui';
import { adminListAiConfigs, adminUpsertAiConfig } from '../api/admin';

const fields: { key: AiConfigKey; label: string; type: 'string' | 'number' | 'json' }[] = [
  { key: 'default_model', label: 'Default LLM model', type: 'string' },
  { key: 'fallback_model', label: 'Fallback LLM model', type: 'string' },
  { key: 'embedding_model', label: 'Embedding model', type: 'string' },
  { key: 'system_prompt_template', label: 'System prompt template', type: 'string' },
  { key: 'max_tokens', label: 'Max tokens per turn', type: 'number' },
  { key: 'temperature', label: 'Temperature', type: 'number' },
  { key: 'rag_top_k', label: 'RAG top-K', type: 'number' },
  { key: 'chunk_size_tokens', label: 'Chunk size (tokens)', type: 'number' },
  { key: 'chunk_overlap_tokens', label: 'Chunk overlap (tokens)', type: 'number' },
  { key: 'tool_definitions', label: 'Tool definitions (JSON)', type: 'json' },
];

export function AiConfigPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-ai-configs'],
    queryFn: adminListAiConfigs,
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const c of data.configs) {
      const f = fields.find((x) => x.key === c.key);
      if (!f) continue;
      if (f.type === 'json') next[c.key] = JSON.stringify(c.value, null, 2);
      else if (f.type === 'number') next[c.key] = String(c.value ?? '');
      else next[c.key] = typeof c.value === 'string' ? c.value : JSON.stringify(c.value);
    }
    setValues(next);
  }, [data]);

  const upsert = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      adminUpsertAiConfig(key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-ai-configs'] }),
  });

  function save(key: AiConfigKey, type: 'string' | 'number' | 'json') {
    const raw = values[key] ?? '';
    let parsed: unknown = raw;
    try {
      if (type === 'number') {
        const n = Number(raw);
        if (!Number.isFinite(n)) throw new Error('Not a number');
        parsed = n;
      } else if (type === 'json') {
        parsed = JSON.parse(raw);
      }
      setErrors((e) => ({ ...e, [key]: '' }));
      setSavingKey(key);
      upsert.mutate({ key, value: parsed }, { onSettled: () => setSavingKey(null) });
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [key]: err instanceof Error ? err.message : 'Invalid value',
      }));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">AI Configuration</h1>
        <p className="text-slate-500 text-sm">
          Tunable settings used by the orchestrator. Models are OpenRouter slugs.
        </p>
      </div>
      {isLoading ? (
        <div className="text-slate-500">Loading...</div>
      ) : (
        <div className="grid gap-3">
          {fields.map((f) => {
            const v = values[f.key] ?? '';
            const isLong = f.type === 'json' || f.key === 'system_prompt_template';
            return (
              <Card key={f.key}>
                <label className="block text-sm font-medium text-slate-800 mb-1">{f.label}</label>
                <div className="text-xs text-slate-500 mb-2">key: <code>{f.key}</code></div>
                {isLong ? (
                  <textarea
                    className="w-full h-44 font-mono text-sm border border-slate-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={v}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                ) : (
                  <input
                    className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={v}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                )}
                {errors[f.key] ? (
                  <div className="text-xs text-red-600 mt-1">{errors[f.key]}</div>
                ) : null}
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => save(f.key, f.type)}
                    loading={savingKey === f.key}
                  >
                    Save
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
